import React from 'react'

import { fetchAtHomeChanges } from 'api/atHome'
import { navigationPaths } from 'app/config'

import { fmtDate, fmtDateTime, fmtTime } from './format'
import AtHomeCrudPage from './shared/AtHomeCrudPage'

/**
 * Bookings → "Cancelled & rescheduled" (owner, 2026-10-19): every cancellation
 * and every reschedule, one row per change, newest first. A cancelled module is
 * ONE row with its session count, not one per session. Read-only: the actions
 * live on the Trial / Regular tabs.
 *
 * Child, category, grade and parent each get their own column so every one is
 * filterable from its header; the parent's name and number open their Alfred
 * profile (Parent Dashboard).
 */

const BY_LABEL = {
  ops: 'Ops',
  parent: 'Parent',
  system: 'System',
  unknown: 'Unknown'
}

const when = (slot) =>
  slot && slot.date
    ? `${fmtDate(slot.date)}${slot.start ? `, ${fmtTime(slot.start)}` : ''}`
    : ''

export const changeRow = (r) => ({
  id: r.id,
  statusLabel: r.type === 'rescheduled' ? 'Rescheduled' : 'Cancelled',
  kindLabel: r.kind === 'regular' ? 'Regular' : 'Trial',
  category: r.category || '',
  session: r.session || '',
  childName: r.child ? r.child.name : '',
  grade: r.grade || '',
  parentName: r.parent ? r.parent.name : '',
  parentPhone: r.parent ? r.parent.phone : '',
  parentUuid: r.parent ? r.parent.uuid : '',
  teacher: r.teacher || '',
  // A cancelled module's row starts at its first cancelled session.
  originalLabel: when(r.from),
  rescheduledLabel: r.type === 'rescheduled' ? when(r.to) : '',
  sessions: r.sessions || 1,
  doneBy:
    r.by && r.by.name
      ? `${BY_LABEL[r.by.role] || r.by.role} · ${r.by.name}`
      : BY_LABEL[r.by && r.by.role] || 'Unknown',
  doneOn: fmtDateTime(r.at),
  reason: r.reason || ''
})

const AtHomeChanges = () => {
  // The table hands a cell renderer the row's index in the data it was given,
  // which is exactly the array fetchList returned; keep it to reach the
  // parent's uuid from the name and number cells.
  const rowsRef = React.useRef([])

  const fetchList = React.useCallback(async (range) => {
    const list = (await fetchAtHomeChanges({}, range)).map(changeRow)
    rowsRef.current = list
    return list
  }, [])

  const parentLink = (field) => ({
    customBodyRenderLite: (i) => {
      const row = rowsRef.current[i]
      if (!row || !row[field]) return '—'
      return row.parentUuid ? (
        <a
          href={`${navigationPaths.parentdashboard}?parent=${row.parentUuid}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ whiteSpace: 'nowrap' }}
        >
          {row[field]}
        </a>
      ) : (
        row[field]
      )
    }
  })

  // Names and order agreed with the owner, 2026-10-19. Rows stay newest
  // first by "Done on" (the API's order) even though it is not the first column.
  const columns = [
    { name: 'statusLabel', label: 'Status' },
    { name: 'kindLabel', label: 'Session type' },
    { name: 'category', label: 'Category' },
    { name: 'session', label: 'Session name' },
    { name: 'childName', label: 'Child name' },
    { name: 'grade', label: 'Grade' },
    {
      name: 'parentName',
      label: 'Parent name',
      options: parentLink('parentName')
    },
    {
      name: 'parentPhone',
      label: 'Parent number',
      options: parentLink('parentPhone')
    },
    { name: 'teacher', label: 'Teacher name' },
    {
      name: 'originalLabel',
      label: 'Original date-time',
      options: { keepOrder: true }
    },
    {
      name: 'rescheduledLabel',
      label: 'Rescheduled date-time',
      options: { keepOrder: true }
    },
    { name: 'sessions', label: 'Sessions' },
    { name: 'doneBy', label: 'Done by' },
    { name: 'doneOn', label: 'Done on', options: { keepOrder: true } },
    { name: 'reason', label: 'Reason', options: { wrap: true } }
  ]

  return (
    <AtHomeCrudPage
      subtitle="Every cancelled and rescheduled session, newest first."
      entityLabel="Change"
      fetchList={fetchList}
      dateRange
      dateRangePlaceholder="Filter date"
      columns={columns}
      idOf={(row) => row.id}
    />
  )
}

export default AtHomeChanges
